import {
  Box,
  Button,
  Card,
  Flex,
  HStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { RiCodeSSlashLine } from "@remixicon/react";
import { AnimatePresence } from "framer-motion";
import { useLocalStorage } from "usehooks-ts";

import CardContainer from "@/common/components/CardContainer";
import MotionBox from "@/common/components/MotionBox";
import { SectionHeading } from "@/common/components/SectionHeading";
import {
  CodeExampleUsecases,
  FeatureCodeExample,
} from "@/feature/components/FeatureCodeExample";
import { useFeatureData } from "@/feature/data/useFeatureData";

function AnimatedWidget({ children }: { children: React.ReactNode }) {
  return (
    <MotionBox
      animate={{ opacity: 1, height: "auto", scale: 1 }}
      exit={{ opacity: 0, height: 0, scale: 0.95 }}
      initial={{ opacity: 0, height: 0, scale: 0.95 }}
      transition={{ duration: "0.15", ease: "easeInOut" }}
    >
      {children}
    </MotionBox>
  );
}

export const SetupInstructionsStorageKey = "reflag-feature-setup-show";

const usecaseTitles: Record<CodeExampleUsecases, string> = {
  usage: "Install usage tracking",
  access: "Install access checking",
  feedback: "Install feedback button",
  config: "Install configuration usage",
  monitor: "Install tried tracking & feedback button",
} as const;

export const useSetupInstructions = () => {
  const [showSetup, setShowSetup] = useLocalStorage(
    SetupInstructionsStorageKey,
    true,
  );

  return [showSetup, setShowSetup] as const;
};

export const SetupInstructions = ({
  featureId,
  usecase = "access",
}: {
  featureId?: string;
  usecase?: CodeExampleUsecases;
}) => {
  const { data: feature, isLoading: isFeatureLoading } =
    useFeatureData(featureId);

  const iconColor = useColorModeValue("blackAlpha.600", "orange.200");
  const titleColor = useColorModeValue(undefined, "orange.200");
  const hideButtonColorScheme = useColorModeValue("blackAlpha", "gray");
  const [showSetup, setShowSetup] = useSetupInstructions();

  const title = usecaseTitles[usecase];

  if (isFeatureLoading || !feature) return null;

  return (
    <AnimatePresence initial={false}>
      {showSetup && (
        <AnimatedWidget key="instructions">
          <CardContainer
            darkModeBorderColor="orange.200"
            gap={1}
            lightModeBgColor="#F7EBDE"
            mb={4}
            position="relative"
          >
            <Flex dir="column" h={8} justify="space-between" px={3} py={0.5}>
              <HStack>
                <Box color={iconColor}>
                  <RiCodeSSlashLine size={16} />
                </Box>
                <SectionHeading color={titleColor}>{title}</SectionHeading>
              </HStack>
              <Button
                colorScheme={hideButtonColorScheme}
                position="absolute"
                right={2}
                top={2}
                variant="ghost"
                onClick={() => setShowSetup(false)}
              >
                Hide
              </Button>
            </Flex>
            <Card direction="row">
              <FeatureCodeExample
                feature={feature}
                featureSource={feature.source}
                sharingPreface="Implement flag in Reflag"
                usecase={usecase}
              />
            </Card>
          </CardContainer>
        </AnimatedWidget>
      )}
    </AnimatePresence>
  );
};
